import { el, showToast } from '../ui/dom';

/**
 * SoftAP Wi-Fi setup (v7 entryWifi). Served when phone is on device hotspot.
 * Tries DEVICE_LOCAL JSON APIs; falls back to classic SoftAP /scan + /submit.
 */
const DEVICE_LOCAL =
  (import.meta.env.VITE_DEVICE_LOCAL as string | undefined)?.replace(/\/$/, '') ||
  'http://192.168.4.1';

type Net = { ssid: string; signal?: string; rssi?: number };

function signalLabel(n: Net): string {
  if (n.signal) return n.signal === 'strong' ? 'Strong signal' : n.signal === 'weak' ? 'Weak signal' : 'Good signal';
  if (n.rssi != null) {
    if (n.rssi >= -55) return 'Strong signal';
    if (n.rssi >= -70) return 'Good signal';
    return 'Weak signal';
  }
  return 'Wi-Fi';
}

async function scanNetworks(): Promise<Net[]> {
  try {
    const r = await fetch(`${DEVICE_LOCAL}/api/wifi/scan`, { method: 'GET' });
    if (r.ok) {
      const j = (await r.json()) as { networks?: Net[] };
      if (j.networks?.length) return j.networks;
    }
  } catch {
    /* try classic SoftAP */
  }
  try {
    const r = await fetch(`${DEVICE_LOCAL}/scan`, { method: 'GET' });
    if (!r.ok) throw new Error('scan failed');
    const j = (await r.json()) as Array<{ ssid?: string; rssi?: number }>;
    return (Array.isArray(j) ? j : [])
      .filter((x) => x.ssid)
      .map((x) => ({ ssid: String(x.ssid), rssi: x.rssi }));
  } catch {
    return [
      { ssid: 'sunnyhome', signal: 'strong' },
      { ssid: 'Guest Wi-Fi', signal: 'good' },
    ];
  }
}

async function connectWifi(ssid: string, password: string): Promise<'ok' | 'bad_password' | 'error'> {
  try {
    const r = await fetch(`${DEVICE_LOCAL}/api/wifi/connect`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ssid, password }),
    });
    if (r.ok) {
      const j = (await r.json()) as { status?: string };
      if (j.status === 'bad_password') return 'bad_password';
      if (j.status === 'ok') return 'ok';
    }
  } catch {
    /* classic */
  }
  try {
    const body = new URLSearchParams({ ssid, password });
    const r = await fetch(`${DEVICE_LOCAL}/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (r.ok || r.status === 302) return 'ok';
    return 'error';
  } catch {
    return 'error';
  }
}

export async function renderWifiSetup(root: HTMLElement): Promise<void> {
  root.innerHTML = '';
  const wrap = el('div', { className: 'wifi-root' });

  const shell = el('div', { className: 'qr-shell' });
  shell.append(el('div', { className: 'qr-brand' }, 'remindo'));

  const card = el('div', { className: 'card' });
  const form = el('div', { id: 'wifiSetupForm' });
  form.append(
    el('div', { className: 'qr-title' }, 'Connect this Remindo to Wi-Fi'),
    el('div', { className: 'qr-copy' }, 'Choose the 2.4 GHz Wi-Fi network this Remindo should use.'),
  );

  const step1 = el('div', { className: 'qr-step' });
  step1.append(el('div', { className: 'qr-step-num' }, '1'));
  const step1Body = el('div', { className: 'qr-step-body' });
  step1Body.append(el('div', { className: 'qr-step-title' }, 'Choose Wi-Fi'));
  const list = el('div', { className: 'device-sel', id: 'wifiNetworkList' });
  list.append(el('div', { className: 'loading-row' }, 'Scanning…'));
  step1Body.append(list);
  step1.append(step1Body);
  form.append(step1);

  const step2 = el('div', { className: 'qr-step' });
  step2.append(el('div', { className: 'qr-step-num' }, '2'));
  const step2Body = el('div', { className: 'qr-step-body' });
  step2Body.append(el('div', { className: 'qr-step-title' }, 'Wi-Fi password'));
  const pwField = el('div', { className: 'field' });
  const pw = el('input', {
    id: 'wifiPasswordInput',
    type: 'password',
    autocomplete: 'current-password',
  }) as HTMLInputElement;
  pwField.append(pw);
  const connectBtn = el('button', { className: 'btn', type: 'button', id: 'wifiConnectBtn' }, 'Connect to Wi-Fi') as HTMLButtonElement;
  step2Body.append(
    pwField,
    el('div', { className: 'card-note' }, 'Passwords are case-sensitive.'),
    connectBtn,
  );
  step2.append(step2Body);
  form.append(step2);

  const connecting = el('div', { className: 'wifi-state', id: 'wifiConnecting' });
  connecting.style.display = 'none';
  connecting.innerHTML = `<div class="wifi-state-icon"><div class="wifi-spinner"></div></div>
    <div class="wifi-state-title">Connecting...</div>
    <div class="wifi-state-copy">Keep this page open.</div>`;

  const success = el('div', { className: 'wifi-state', id: 'wifiSuccess' });
  success.style.display = 'none';
  success.innerHTML = `<div class="wifi-state-icon ok">✓</div>
    <div class="wifi-state-title">Setup complete</div>
    <div class="wifi-state-copy">You can close this page.</div>`;

  const error = el('div', { className: 'wifi-state', id: 'wifiError' });
  error.style.display = 'none';
  const errCopy = el('div', { className: 'wifi-state-copy' }, 'Check the Wi-Fi password and try again.');
  const errActions = el('div', { className: 'wifi-actions' });
  const startOver = el('button', { className: 'btn btn-ghost', type: 'button' }, 'Start over') as HTMLButtonElement;
  const tryAgain = el('button', { className: 'btn', type: 'button' }, 'Try again') as HTMLButtonElement;
  errActions.append(startOver, tryAgain);
  error.append(
    el('div', { className: 'wifi-state-icon error' }, '!'),
    el('div', { className: 'wifi-state-title' }, "Couldn't connect"),
    errCopy,
    errActions,
  );

  card.append(form, connecting, success, error);
  shell.append(card);
  wrap.append(shell);
  root.append(wrap);

  let selectedSsid = '';

  const show = (which: 'form' | 'connecting' | 'success' | 'error') => {
    form.style.display = which === 'form' ? 'block' : 'none';
    connecting.style.display = which === 'connecting' ? 'flex' : 'none';
    success.style.display = which === 'success' ? 'flex' : 'none';
    error.style.display = which === 'error' ? 'flex' : 'none';
  };

  const renderList = (nets: Net[]) => {
    list.innerHTML = '';
    if (!nets.length) {
      list.append(el('div', { className: 'error-row' }, 'No networks found. Refresh the page.'));
      return;
    }
    nets.forEach((n, i) => {
      const row = el('div', { className: `device-opt${i === 0 ? ' on' : ''}` });
      row.onclick = () => {
        list.querySelectorAll('.device-opt').forEach((el) => el.classList.remove('on'));
        row.classList.add('on');
        selectedSsid = n.ssid;
      };
      row.innerHTML = `<div class="device-opt-icon">Wi</div>
        <div style="flex:1;min-width:0;">
          <div class="device-opt-name">${escapeHtml(n.ssid)}</div>
          <div class="device-opt-loc">${escapeHtml(signalLabel(n))}</div>
        </div>
        <div class="device-opt-check"></div>`;
      list.append(row);
      if (i === 0) selectedSsid = n.ssid;
    });
  };

  const doConnect = async () => {
    if (!selectedSsid) {
      showToast('Choose a Wi-Fi network');
      return;
    }
    show('connecting');
    const copy = connecting.querySelector('.wifi-state-copy');
    if (copy) {
      copy.innerHTML = `Connecting this Remindo to <strong>${escapeHtml(selectedSsid)}</strong>. Keep this page open.`;
    }
    const status = await connectWifi(selectedSsid, pw.value);
    if (status === 'ok') {
      const sc = success.querySelector('.wifi-state-copy');
      if (sc) {
        sc.innerHTML = `This Remindo is connected to <strong>${escapeHtml(selectedSsid)}</strong>. You can close this page.`;
      }
      show('success');
    } else {
      errCopy.textContent =
        status === 'bad_password'
          ? 'Check the Wi-Fi password and try again.'
          : "Couldn't connect. Start over from the Remindo Wi-Fi screen.";
      show('error');
    }
  };

  connectBtn.onclick = () => void doConnect();
  tryAgain.onclick = () => void doConnect();
  startOver.onclick = () => {
    pw.value = '';
    show('form');
  };

  const nets = await scanNetworks();
  renderList(nets);
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
